/**
 * 折线图组件
 *
 * 使用 ECharts 实现的折线图，用于展示时间序列数据趋势
 */

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { LineChart as EChartsLineChart, LineSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { TimeSeriesDataItem } from '@/types/analysis';
import './LineChart.css';

echarts.use([
  EChartsLineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  CanvasRenderer,
]);

interface LineChartProps {
  data: TimeSeriesDataItem[];
  height?: number;
  title?: string;
  loading?: boolean;
}

export function LineChart({ data, height = 400, title, loading = false }: LineChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    // 初始化图表实例
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;

    // 检查实例是否已被销毁
    if (!chart || chart.isDisposed()) return;

    // 配置图表选项
    const option: echarts.EChartsCoreOption = {
      title: {
        text: title,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: '#1F1F1F',
        },
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#E8E8E8',
        borderWidth: 1,
        textStyle: {
          color: '#5C5C5C',
        },
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params;
          return `${param.axisValue}<br/>反馈量: <strong>${param.value}</strong>`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: title ? '15%' : '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map((item) => item.date),
        axisLine: {
          lineStyle: {
            color: '#D9D9D9',
          },
        },
        axisLabel: {
          color: '#8C8C8C',
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#D9D9D9',
          },
        },
        axisLabel: {
          color: '#8C8C8C',
          fontSize: 12,
        },
        splitLine: {
          lineStyle: {
            color: '#F0F0F0',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          type: 'line',
          data: data.map((item) => item.value),
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: '#1677FF',
            width: 2,
          },
          itemStyle: {
            color: '#1677FF',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(22, 119, 255, 0.3)' },
              { offset: 1, color: 'rgba(22, 119, 255, 0.05)' },
            ]),
          },
        } as LineSeriesOption,
      ],
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          type: 'slider',
          start: 0,
          end: 100,
          height: 20,
          bottom: 5,
        },
      ],
    };

    chart.setOption(option, { notMerge: false });

    // 响应式调整
    const handleResize = () => {
      if (chart && !chart.isDisposed()) {
        chart.resize();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, title, loading]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="line-chart-loading" style={{ height }}>
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="line-chart-empty" style={{ height }}>
        <div className="empty-message">暂无数据</div>
      </div>
    );
  }

  return <div ref={chartRef} className="line-chart" style={{ height }} />;
}
